import {
  FlexContainers,
  GradientLoader,
  IconButton,
  Paragraph2,
  SVG,
  WrapperContainer
} from "../../../../components";
import {
  IconContainer,
  MessagePreviewWrapper,
  Preview,
  PreviewMessage,
  PreviewTitle
} from "./styled";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo } from "react";
import { log, t } from "../../../../services";
import { alignItems, crossPageNotifications, routePaths, strCode } from "../../../../consts";
import { dispatch } from "../../../../state/store";
import { setNotificationsRefresh } from "../../../../state/ducks";
import { useSelector } from "react-redux";

export const MessagePreview = ({ notification }) => {
  const loading = useSelector((state) => state.notifications?.loading);
  const englishOrFirstLocalization = useMemo(() => {
    if (!notification?.localizations || loading) {
      return null;
    }

    const localizations = notification.localizations;

    return localizations.find((l) => l.language === "EN") || localizations[0];
  }, [notification]);

  const handleRefresh = useCallback(() => {
    dispatch(setNotificationsRefresh(true));
  }, []);

  const handleEdit = useCallback(() => {
    window.open(
      `${window.location.origin}${routePaths.NOTIFICATIONS_EDIT}/${notification.notificationName}`,
      "_blank"
    );
  }, [notification?.notificationName]);

  const handleMessage = useCallback(
    (event) => {
      if (event.origin !== window.location.origin) return;

      const data = event.data;

      if (data?.type === crossPageNotifications.notificationMessageUpdate) {
        log.trace("MessagePreview: Received message from child page " + JSON.stringify(data));
      }

      if (
        data?.type === crossPageNotifications.notificationMessageUpdate &&
        data?.payload === notification?.notificationName &&
        !loading
      ) {
        handleRefresh();
      }
    },
    [notification?.notificationName, loading]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);

  return (
    <MessagePreviewWrapper>
      <Paragraph2>{t(strCode.MESSAGE_PREVIEW_TITLE)}</Paragraph2>

      <Preview>
        <SVG.UnionIcon />

        <WrapperContainer
          Container={FlexContainers.FlexColumnStart}
          gap={4}
          alignItems={alignItems.start}
          width={"100%"}>
          <WrapperContainer Container={FlexContainers.FlexRowBetween} width={"100%"}>
            {loading ? (
              <GradientLoader width={"20%"} height={"24px"} />
            ) : (
              <>
                <PreviewTitle>{englishOrFirstLocalization.title}</PreviewTitle>

                <IconContainer>
                  <IconButton hasHoverCircle onClick={handleEdit}>
                    <SVG.EditIcon />
                  </IconButton>
                  <IconButton hasHoverCircle onClick={handleRefresh}>
                    <SVG.RefreshIcon />
                  </IconButton>
                </IconContainer>
              </>
            )}
          </WrapperContainer>
          {loading ? (
            <GradientLoader width={"80%"} height={"24px"} />
          ) : (
            <PreviewMessage>{englishOrFirstLocalization.bodyText}</PreviewMessage>
          )}
        </WrapperContainer>
      </Preview>
    </MessagePreviewWrapper>
  );
};

MessagePreview.propTypes = {
  notification: PropTypes.shape({
    createdBy: PropTypes.shape({
      createdTs: PropTypes.string.isRequired,
      sender: PropTypes.string.isRequired
    }).isRequired,
    destination: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    imageFile: PropTypes.object.isRequired,
    imageSrc: PropTypes.string.isRequired,
    localizations: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        language: PropTypes.string.isRequired,
        bodyText: PropTypes.string.isRequired
      })
    ).isRequired,
    notificationName: PropTypes.string.isRequired
  }).isRequired
};
